const toTimestamp = (value?: string | null): number => {
    if (!value) return Number.NEGATIVE_INFINITY;
    const timestamp = new Date(value).getTime();
    return Number.isFinite(timestamp) ? timestamp : Number.NEGATIVE_INFINITY;
};

export const isWorkspaceSubscriptionExpired = (
    subscription?: {
        status?: string | null;
        current_period_end?: string | null;
        plan_label?: string | null;
    } | null,
): boolean => {
    if (!subscription) return false;

    const status = (subscription.status || '').toLowerCase();
    const now = Date.now();
    const currentPeriodEnd = subscription.current_period_end
        ? new Date(subscription.current_period_end).getTime()
        : null;

    return Boolean(
        subscription.plan_label === 'Plan Expired'
        || status === 'expired'
        || status === 'canceled'
        || (currentPeriodEnd && now > currentPeriodEnd)
    );
};

export const isActiveWorkspaceSubscriptionStatus = (status?: string | null): boolean => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'active' || normalized === 'paid';
};

const isNonTrialTerminalOrRenewalStatus = (status?: string | null): boolean => {
    const normalized = (status || '').toLowerCase();
    return normalized === 'pending_cancellation'
        || normalized === 'canceled'
        || normalized === 'expired';
};

export const isWorkspaceSubscriptionTrial = (
    subscription?: {
        status?: string | null;
        is_in_trial?: boolean | null;
        trial_end_at?: string | null;
        plan_badge?: string | null;
    } | null,
): boolean => {
    if (!subscription) return false;

    const normalizedStatus = (subscription.status || '').toLowerCase();
    const trialEndAt = subscription.trial_end_at
        ? new Date(subscription.trial_end_at).getTime()
        : null;
    const isCurrentTrialWindow = Boolean(trialEndAt && Date.now() < trialEndAt);

    if (subscription.is_in_trial !== undefined) {
        return Boolean(subscription.is_in_trial && isCurrentTrialWindow);
    }

    if (isNonTrialTerminalOrRenewalStatus(normalizedStatus)) {
        return false;
    }

    if (isActiveWorkspaceSubscriptionStatus(normalizedStatus)) {
        return false;
    }

    return Boolean(
        isCurrentTrialWindow
        && (
            normalizedStatus === 'trial'
            || normalizedStatus === 'trialing'
            || subscription.plan_badge === 'Trial'
        )
    );
};

export const isExpiredWorkspaceTrialSubscription = (
    subscription?: {
        status?: string | null;
        current_period_end?: string | null;
        trial_end_at?: string | null;
        plan_label?: string | null;
    } | null,
): boolean => {
    if (!subscription?.trial_end_at) return false;

    const trialEndAt = new Date(subscription.trial_end_at).getTime();
    if (!Number.isFinite(trialEndAt)) return false;

    const currentPeriodEnd = subscription.current_period_end
        ? new Date(subscription.current_period_end).getTime()
        : null;
    const normalizedStatus = (subscription.status || '').toLowerCase();
    const hasExpiredState = Boolean(
        subscription.plan_label === 'Plan Expired'
        || normalizedStatus === 'expired'
        || normalizedStatus === 'canceled'
        || (currentPeriodEnd && Date.now() > currentPeriodEnd)
    );

    if (!hasExpiredState) return false;

    return !currentPeriodEnd || trialEndAt >= currentPeriodEnd;
};

export const pickLatestWorkspaceSubscription = <T extends { created_at?: string | null }>(
    subscriptions?: T[] | null,
): T | null => {
    if (!subscriptions?.length) return null;

    return subscriptions.reduce((latest, candidate) => {
        if (!latest) return candidate;
        return toTimestamp(candidate.created_at) > toTimestamp(latest.created_at) ? candidate : latest;
    }, subscriptions[0] ?? null);
};
